/**
 * Tipos para el módulo DDJJ (Declaraciones Juradas) — Fase 4.3
 *
 * Documentos relacionados:
 *   - requerimientos.md § M8 (DDJJ)
 *   - DECISIONES.md #006 (cobranza manual), #015 (aportes extra), #017 (relaciones múltiples)
 */

// ── Estados ───────────────────────────────────────────────────────────────────

export type DDJJEstado = 'borrador' | 'confirmada' | 'adeudada' | 'pagada' | 'parcial';

export function getEstadoLabel(estado: DDJJEstado): string {
  const labels: Record<DDJJEstado, string> = {
    borrador:   'Borrador',
    confirmada: 'Confirmada',
    adeudada:   'Adeudada',
    pagada:     'Pagada',
    parcial:    'Parcial',
  };
  return labels[estado] ?? estado;
}

export function getEstadoColor(
  estado: DDJJEstado
): 'default' | 'warning' | 'success' | 'error' | 'info' | 'primary' {
  switch (estado) {
    case 'borrador':   return 'default';
    case 'confirmada': return 'primary';
    case 'adeudada':   return 'error';
    case 'pagada':     return 'success';
    case 'parcial':    return 'warning';
    default:           return 'default';
  }
}

// ── Entidades ─────────────────────────────────────────────────────────────────

export interface PlanillaDDJJ {
  id: string;
  empresa_id: string;
  periodo_id: string;
  /** Número de secuencia dentro del mismo empresa+período (1, 2, 3…) */
  numero: number;
  estado: DDJJEstado;
  monto_total: number;
  interes: number;
  pagos_acumulados: number;
  saldo: number;
  archivo_url: string | null;
  fecha_carga: string;
  fecha_confirmacion: string | null;
  confirmada_por: string | null;
  observaciones: string | null;
  created_at: string;
  updated_at: string;
  // Joins (cuando se solicitan explícitamente)
  empresa?: { id: string; razon_social: string; cuit: string } | null;
  periodo?: { id: string; periodo: string; descripcion: string | null } | null;
}

export interface PlanillaDetalle {
  id: string;
  planilla_id: string;
  afiliado_id: string | null;
  relacion_laboral_id: string | null;
  cuil: string;
  apellido: string;
  nombre: string;
  categoria_id: string | null;
  es_afiliado: boolean;
  sueldo: number;
  porcentaje_aporte: number | null;
  aporte: number;
  observaciones: string | null;
  created_at: string;
  // Join
  categoria?: { descripcion: string } | null;
}

/**
 * Fila devuelta por la función SQL `calcular_planilla_prefill`.
 * Se usa para pre-poblar el detalle al crear una nueva DDJJ.
 */
export interface PlanillaPrefillRow {
  afiliado_id: string;
  relacion_laboral_id: string;
  cuil: string;
  apellido: string;
  nombre: string;
  categoria_id: string | null;
  categoria_descripcion: string | null;
  es_afiliado: boolean;
  porcentaje_aporte: number;
  concepto_aporte_id: string | null;
}
